import i18n from '@/libs/i18n'

/**
 * Error translation for the spec-aligned APIs.
 *
 * Both user-api and auth-api now answer errors as
 * `{ "error": { "code": N, "message": "..." } }`, where codes below 100 are
 * generic and codes from 100 on are service-specific. The numbering here
 * mirrors `pkg/enums/Error` in each service — keep them in sync.
 *
 * Historically user-api serialized a raw Go error (which marshals to `{}`) or
 * plain text, and auth-api answered `{ "error": "<english message>" }`, so both
 * legacy shapes are still accepted: the frontend and the APIs can be released
 * in either order instead of requiring a lock-step deploy.
 */

const t = (key: string): string => i18n.global.t(key)

/** Generic codes shared by the aligned services (pkg/enums/Error, < 100). */
enum GenericErrors {
    InternalServerError = 1,
    InvalidData = 2,
    NotFound = 3,
    Unauthorized = 4,
    /** user-api calls 5 Forbidden; auth-api calls it FailedToInsert. */
    ForbiddenOrFailedToInsert = 5
}

/** user-api domain codes (src/user-api/pkg/enums/Error). */
enum UserApiErrors {
    EmptyObjectKey = 100,
    EmptyUnitKey = 101,
    EmptyDocumentType = 102,
    EmptyDepositoryId = 103,
    EmptyDistributorType = 104,
    EmptySupplier = 105,
    EmptyDate = 106,
    WrongDistributorType = 107,
    WrongDate = 108,
    WrongType = 109,
    UserNotOwnUnit = 110,
    UserNotOwnObject = 111,
    UserNotOwnDocument = 112,
    NoAccessToDocuments = 113,
    NoAccess = 114
}

/** auth-api domain codes (src/auth-api/pkg/enums/Error). */
enum AuthApiErrors {
    InvalidBillPassword = 100,
    UserExistByEmail = 101,
    UserExistByCodePayerAndManager = 102,
    NoCodePayerForManagerExists = 103,
    ManagerMissing = 104,
    UserNotConnectedToManager = 105,
    InvalidCaptcha = 106,
    ForgottenPasswordRequestExpired = 107,
    InvalidPassword = 108,
    InvalidEmail = 109
}

/** Legacy auth-api contract: `{ "error": "<english message>" }`. */
enum LegacyAuthMessages {
    InternalServerError = 'Unknown internal server error',
    InvalidData = 'Invalid data received',
    InvalidBillPassword = 'Invalid bill password',
    UserExistByEmail = 'User with this email exists',
    UserExistByCodePayerAndManager = 'User with this code exists',
    NoCodePayerForManagerExists = 'Code payer for this manager does not exist',
    ManagerMissing = 'Manager data is missing',
    UserNotConnectedToManager = 'User is not connected to manager',
    InvalidCaptcha = 'Invalid captcha',
    ForgottenPasswordRequestExpired = 'Forgotten password request expired',
    InvalidPassword = 'Invalid password'
}

interface ApiError {
    code?: number
    message?: string
}

/**
 * Pulls the error payload out of a response body, accepting the aligned
 * `{ error: { code, message } }`, a bare `{ code, message }`, and the legacy
 * auth-api `{ error: "<message>" }`. Also unwraps an axios error, whose body
 * lives under `response.data`.
 */
function extractApiError(input: unknown): ApiError | null {
    if (input === null || typeof input !== 'object') return null

    // axios error -> response body
    const response = (input as { response?: unknown }).response
    let body: unknown = input
    if (response !== null && typeof response === 'object' && 'data' in response) {
        body = (response as { data: unknown }).data
    }

    if (body === null || typeof body !== 'object') return null

    const wrapped = (body as { error?: unknown }).error

    // legacy auth-api: error is the message string itself
    if (typeof wrapped === 'string') return { message: wrapped }

    const candidate = (wrapped !== null && typeof wrapped === 'object') ? wrapped : body
    const { code, message } = candidate as { code?: unknown, message?: unknown }

    if (typeof code !== 'number' && typeof message !== 'string') return null

    return {
        code: typeof code === 'number' ? code : undefined,
        message: typeof message === 'string' ? message : undefined
    }
}

function genericMessage(code: number): string | null {
    switch (code as GenericErrors) {
        case GenericErrors.InvalidData:
            return t('api_errors.invalid_data')
        case GenericErrors.NotFound:
            return t('api_errors.not_found')
        case GenericErrors.Unauthorized:
            return t('api_errors.not_signed_in')
        case GenericErrors.ForbiddenOrFailedToInsert:
            return t('api_errors.forbidden')
        case GenericErrors.InternalServerError:
        default:
            return null
    }
}

function userApiMessage(code: number): string | null {
    switch (code as UserApiErrors) {
        case UserApiErrors.EmptyObjectKey:
            return t('api_errors.empty_object_key')
        case UserApiErrors.EmptyUnitKey:
            return t('api_errors.empty_unit_key')
        case UserApiErrors.EmptyDocumentType:
            return t('api_errors.empty_document_type')
        case UserApiErrors.EmptyDepositoryId:
            return t('api_errors.empty_depository')
        case UserApiErrors.EmptyDistributorType:
            return t('api_errors.empty_distributor_type')
        case UserApiErrors.EmptySupplier:
            return t('api_errors.empty_supplier')
        case UserApiErrors.EmptyDate:
            return t('api_errors.empty_date')
        case UserApiErrors.WrongDistributorType:
            return t('api_errors.wrong_distributor_type')
        case UserApiErrors.WrongDate:
            return t('api_errors.wrong_date')
        case UserApiErrors.WrongType:
            return t('api_errors.wrong_type')
        case UserApiErrors.UserNotOwnUnit:
            return t('api_errors.not_own_unit')
        case UserApiErrors.UserNotOwnObject:
            return t('api_errors.not_own_object')
        case UserApiErrors.UserNotOwnDocument:
            return t('api_errors.not_own_document')
        case UserApiErrors.NoAccessToDocuments:
            return t('api_errors.no_access_to_documents')
        case UserApiErrors.NoAccess:
            return t('api_errors.no_access')
        default:
            return null
    }
}

function authApiMessage(code: number): string | null {
    switch (code as AuthApiErrors) {
        case AuthApiErrors.InvalidBillPassword:
            return t('api_errors.invalid_bill_password')
        case AuthApiErrors.UserExistByEmail:
            return t('api_errors.user_exists_by_email')
        case AuthApiErrors.UserExistByCodePayerAndManager:
            return t('api_errors.user_exists_by_code_payer')
        case AuthApiErrors.NoCodePayerForManagerExists:
            return t('api_errors.no_code_payer')
        case AuthApiErrors.ManagerMissing:
            return t('api_errors.manager_missing')
        case AuthApiErrors.UserNotConnectedToManager:
            return t('api_errors.user_not_connected_to_manager')
        case AuthApiErrors.InvalidCaptcha:
            return t('api_errors.invalid_captcha')
        case AuthApiErrors.ForgottenPasswordRequestExpired:
            return t('api_errors.forgotten_password_expired')
        case AuthApiErrors.InvalidPassword:
            return t('api_errors.invalid_password')
        case AuthApiErrors.InvalidEmail:
            return t('api_errors.invalid_email')
        default:
            return null
    }
}

function legacyAuthMessage(message: string): string | null {
    switch (message as LegacyAuthMessages) {
        case LegacyAuthMessages.InternalServerError:
            return null
        case LegacyAuthMessages.InvalidData:
            return t('api_errors.invalid_data')
        case LegacyAuthMessages.InvalidBillPassword:
            return t('api_errors.invalid_bill_password')
        case LegacyAuthMessages.UserExistByEmail:
            return t('api_errors.user_exists_by_email')
        case LegacyAuthMessages.UserExistByCodePayerAndManager:
            return t('api_errors.user_exists_by_code_payer')
        case LegacyAuthMessages.NoCodePayerForManagerExists:
            return t('api_errors.no_code_payer')
        case LegacyAuthMessages.ManagerMissing:
            return t('api_errors.manager_missing')
        case LegacyAuthMessages.UserNotConnectedToManager:
            return t('api_errors.user_not_connected_to_manager')
        case LegacyAuthMessages.InvalidCaptcha:
            return t('api_errors.invalid_captcha')
        case LegacyAuthMessages.ForgottenPasswordRequestExpired:
            return t('api_errors.forgotten_password_expired')
        case LegacyAuthMessages.InvalidPassword:
            return t('api_errors.invalid_password')
        default:
            return null
    }
}

function translate(input: unknown, fallback: string, domain: (code: number) => string | null): string {
    const apiError = extractApiError(input)

    if (apiError !== null) {
        if (apiError.code !== undefined) {
            const translated = apiError.code >= 100 ? domain(apiError.code) : genericMessage(apiError.code)
            if (translated !== null) return translated
        } else if (apiError.message !== undefined) {
            const translated = legacyAuthMessage(apiError.message)
            if (translated !== null) return translated
        }
    }

    return (fallback && fallback !== '') ? fallback : t('errors.generic_error')
}

/** Translates a user-api error response. */
export function parseError(errorObject: unknown, fallback: string) {
    return translate(errorObject, fallback, userApiMessage)
}

/** Translates an auth-api error response (aligned or legacy string form). */
export function parseErrorAuth(errorObject: unknown, fallback: string) {
    return translate(errorObject, fallback, authApiMessage)
}
